import { useMutation, useQuery } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'

import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  Field,
  Input,
  Select,
  Spinner,
} from '@aeris/ui'

import {
  ApiError,
  fetchCategorySpec,
  fetchCategorySuggestions,
  saveChannelListingDraft,
  type ChannelListingSummaryType,
} from '../lib/api.ts'

/**
 * A ficha de um anúncio: título, categoria e o que a categoria exige (F111).
 *
 * ## Por que a categoria vem primeiro
 *
 * Porque ela decide todo o resto. É a categoria que diz quais atributos são
 * obrigatórios, qual o tamanho máximo do título e quanto o canal cobra de
 * comissão — então um formulário que pedisse os atributos antes dela pediria
 * campos que podem nem existir.
 *
 * O canal sugere, e a pessoa escolhe. Sugerir é melhor que deixar procurar: o
 * marketplace tem o histórico de bilhões de anúncios e acerta na primeira quase
 * sempre. Escolher continua sendo da pessoa porque "quase sempre" não é
 * sempre — o preditor devolveu "Águas Minerais" para "capa de celular samsung
 * a20" na segunda posição.
 */

export type ListingDraftFormProps = {
  open: boolean
  channelId: string
  listing: ChannelListingSummaryType
  onOpenChange: (open: boolean) => void
  onSaved: () => Promise<void> | void
}

export const ListingDraftForm = ({
  open,
  channelId,
  listing,
  onOpenChange,
  onSaved,
}: ListingDraftFormProps): ReactNode => {
  /* Semeado uma vez: o chamador monta isto com `key` na linha sendo editada. */
  const [title, setTitle] = useState(listing.title ?? '')
  const [categoryRef, setCategoryRef] = useState(listing.categoryRef ?? '')
  const [categoryLabel, setCategoryLabel] = useState(
    listing.categoryLabel ?? ''
  )
  const [values, setValues] = useState<Record<string, string>>(
    Object.fromEntries(
      listing.attributes.map((attribute) => [attribute.key, attribute.value])
    )
  )
  const [error, setError] = useState<string | null>(null)

  const suggestions = useQuery({
    queryKey: ['category-suggestions', channelId, listing.id],
    queryFn: async ({ signal }) =>
      await fetchCategorySuggestions(
        channelId,
        { title: title.trim() === '' ? listing.productName : title },
        signal
      ),
    enabled: open,
  })

  /*
   * A ficha da categoria só é buscada quando há categoria.
   *
   * Sem `enabled`, a tela pediria `/categories/` com uma referência vazia toda
   * vez que alguém abrisse o formulário de um anúncio novo — uma chamada que
   * só pode falhar.
   */
  const spec = useQuery({
    queryKey: ['category-spec', channelId, categoryRef],
    queryFn: async ({ signal }) =>
      await fetchCategorySpec(channelId, categoryRef, signal),
    enabled: open && categoryRef !== '',
  })

  const maxTitle = spec.data?.maxTitleLength ?? 200

  const save = useMutation({
    mutationFn: async () => {
      await saveChannelListingDraft(channelId, listing.id, {
        title: title.trim() === '' ? null : title.trim().slice(0, maxTitle),
        categoryRef: categoryRef === '' ? null : categoryRef,
        categoryLabel: categoryLabel === '' ? null : categoryLabel,
        listingType: listing.listingType,
        /*
         * Só o que tem valor.
         *
         * Um atributo com o campo em branco não é "vazio", é ausente — e a
         * prontidão precisa vê-lo faltando para dizer que falta.
         */
        attributes: Object.entries(values)
          .filter(([, value]) => value.trim() !== '')
          .map(([key, value]) => ({ key, value: value.trim() })),
        version: listing.version,
      })
    },
    onSuccess: async () => {
      await onSaved()
      onOpenChange(false)
    },
    onError: (cause: unknown) => {
      setError(
        cause instanceof ApiError
          ? cause.message
          : 'Não foi possível salvar a ficha deste anúncio.'
      )
    },
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        title={`Ficha do anúncio — ${listing.productName}`}
        description="O que o canal precisa saber para aceitar este anúncio. A categoria decide o resto."
        className="max-w-xl"
      >
        <form
          noValidate
          className="flex max-h-[70vh] flex-col gap-4 overflow-y-auto"
          onSubmit={(event) => {
            event.preventDefault()
            save.mutate()
          }}
        >
          <Field
            label="Categoria no canal"
            required
            hint="O canal sugere pelo nome do produto. A comissão depende dela."
          >
            {(field) => (
              <Select
                {...field}
                value={categoryRef}
                onChange={(event) => {
                  const chosen = suggestions.data?.suggestions.find(
                    (suggestion) => suggestion.ref === event.target.value
                  )
                  setCategoryRef(event.target.value)
                  setCategoryLabel(chosen?.label ?? categoryLabel)
                }}
              >
                <option value="">Escolha uma categoria</option>
                {/*
                  A que já está gravada entra na lista mesmo quando o preditor
                  não a sugere agora: senão abrir o formulário de um anúncio
                  classificado o desclassificaria em silêncio.
                */}
                {categoryRef !== '' &&
                !(suggestions.data?.suggestions ?? []).some(
                  (suggestion) => suggestion.ref === categoryRef
                ) ? (
                  <option value={categoryRef}>
                    {categoryLabel === '' ? categoryRef : categoryLabel} (atual)
                  </option>
                ) : null}
                {(suggestions.data?.suggestions ?? []).map((suggestion) => (
                  <option key={suggestion.ref} value={suggestion.ref}>
                    {suggestion.label}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          {spec.data?.listingAllowed === false ? (
            <p role="alert" className="text-(length:--text-sm) text-(--danger)">
              O canal não aceita anúncios novos nesta categoria. Escolha outra.
            </p>
          ) : null}

          <Field
            label="Título do anúncio"
            hint={
              spec.data === undefined
                ? 'Em branco: usa o nome do produto.'
                : `Em branco: usa o nome do produto. O canal aceita até ${String(maxTitle)} caracteres.`
            }
          >
            {(field) => (
              <Input
                {...field}
                value={title}
                maxLength={maxTitle}
                onChange={(event) => {
                  setTitle(event.target.value)
                }}
              />
            )}
          </Field>

          {categoryRef === '' ? (
            <p className="text-(length:--text-sm) text-(--text-muted)">
              Escolha a categoria para ver o que o canal exige.
            </p>
          ) : spec.isPending ? (
            <Spinner />
          ) : spec.isError ? (
            <p role="alert" className="text-(length:--text-sm) text-(--danger)">
              O canal não respondeu o que esta categoria exige. Tente de novo.
            </p>
          ) : (
            <div className="flex flex-col gap-3 border-t border-(--border-subtle) pt-4">
              <h3 className="text-(length:--text-sm) font-medium text-(--text-primary)">
                O que esta categoria exige
              </h3>

              {/*
                Só os obrigatórios e os que já têm valor.
                
                Uma categoria do Mercado Livre tem SETENTA E TRÊS atributos. Uma
                tela com setenta e três campos é uma tela que ninguém preenche,
                e o que decide se o anúncio é aceito são os três obrigatórios.
              */}
              {spec.data.attributes
                .filter(
                  (attribute) =>
                    attribute.required ||
                    (values[attribute.id] ?? '').trim() !== ''
                )
                .map((attribute) => (
                  <Field
                    key={attribute.id}
                    label={attribute.label}
                    required={attribute.required}
                    hint={attribute.hint ?? undefined}
                  >
                    {(field) =>
                      attribute.allowedValues.length > 0 ? (
                        <Select
                          {...field}
                          value={values[attribute.id] ?? ''}
                          onChange={(event) => {
                            setValues((current) => ({
                              ...current,
                              [attribute.id]: event.target.value,
                            }))
                          }}
                        >
                          <option value="">—</option>
                          {attribute.allowedValues.map((value) => (
                            <option key={value} value={value}>
                              {value}
                            </option>
                          ))}
                        </Select>
                      ) : (
                        <Input
                          {...field}
                          value={values[attribute.id] ?? ''}
                          maxLength={attribute.maxLength ?? 255}
                          onChange={(event) => {
                            setValues((current) => ({
                              ...current,
                              [attribute.id]: event.target.value,
                            }))
                          }}
                        />
                      )
                    }
                  </Field>
                ))}
            </div>
          )}

          {error === null ? null : (
            <p role="alert" className="text-(length:--text-sm) text-(--danger)">
              {error}
            </p>
          )}

          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="ghost">
                Cancelar
              </Button>
            </DialogClose>
            <Button type="submit" disabled={save.isPending}>
              Salvar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
